import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Alert, Button, LoadingState } from './ui/Primitives';
import FeatureSection from './SettingsComponents/FeatureSection';
import MessageDeliverySettings from './SettingsComponents/MessageDeliverySettings';
import ManageOrgMembersSection from './SettingsComponents/ManageOrgMembersSection';
import CalendarIntegrations from './CalendarIntegrations';
import CRMIntegrations from '../pages/CRMIntegrations';
import { getBackendHost } from '../utils/backendHost';
import { memberProfileFromResponse } from '../utils/sessionData';
import { GOALS } from '../utils/onboardingFlow';

const authenticated = () => Boolean(localStorage.getItem('jwt_authorization'));

export default function OnboardingGoalSetup({ goals, paths, onRefresh, onBusyChange, onReviewChange, onBack }) {
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [editorBusy, setEditorBusy] = useState(false);
  const [deliveryBusy, setDeliveryBusy] = useState(false);
  const [directoryBusy, setDirectoryBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [stepIndex, setStepIndex] = useState(() => {
    const goal = new URLSearchParams(window.location.search).get('setupGoal');
    const index = goals.indexOf(goal);
    return index < 0 ? 0 : goals.slice(0, index).reduce((count, item) => count + (item === 'faq' ? 0 : ['custom', 'appointment'].includes(item) ? 2 : 1), 0);
  });
  const [connectionNotice] = useState(() => new URLSearchParams(window.location.search).get('setupStatus'));
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('setupStatus');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);
  const needsDelivery = goals.some(goal => ['message', 'routing', 'faq'].includes(goal));
  const load = useCallback(async () => {
    setRefreshing(true);
    setError('');
    try {
      const jwt_token = localStorage.getItem('jwt_authorization');
      const [member, organization] = await Promise.all([
        axios.post(`${getBackendHost()}/user`, { jwt_token }),
        axios.post(`${getBackendHost()}/organization`, { jwt_token }),
      ]);
      setSession({ member: memberProfileFromResponse(member.data), organization: organization.data.organization });
      setRevision(value => value + 1);
      await onRefresh();
    } catch (_error) {
      setError('Setup settings could not be loaded. Please try again.');
    } finally {
      setRefreshing(false);
    }
  }, [onRefresh]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { onBusyChange(Boolean(!session || refreshing || editorBusy || deliveryBusy || directoryBusy)); }, [session, refreshing, editorBusy, deliveryBusy, directoryBusy, onBusyChange]);
  const updateOrganization = useCallback(organization => {
    setSession(current => ({ ...current, organization }));
    setRevision(value => value + 1);
  }, []);
  const steps = useMemo(() => {
    const result = goals.flatMap(goal => {
      const label = GOALS.find(item => item.id === goal)?.title;
      if (goal === 'custom') return [
        { goal, kind: 'connection', title: 'Connect your system', label },
        { goal, kind: 'service', title: 'Set up your custom workflow', label },
      ];
      if (goal === 'appointment') return [
        { goal, kind: 'connection', title: 'Connect your calendar', label },
        { goal, kind: 'service', title: 'Set up appointment booking', label },
      ];
      if (goal === 'faq') return [];
      return [{ goal, kind: 'service', title: goal === 'routing' ? 'Set up call routing' : 'Set up message taking', label }];
    });
    if (needsDelivery) result.push(
      { kind: 'delivery', title: 'Who should receive general messages?', label: 'Shared settings' },
      { kind: 'team', title: goals.includes('routing') ? 'Set transfer destinations and notifications' : 'Set team notifications', label: 'Shared settings' },
    );
    result.push({ kind: 'review', title: 'Review your setup', label: 'Review' });
    return result;
  }, [goals, needsDelivery]);
  const step = steps[Math.min(stepIndex, steps.length - 1)];
  const busy = refreshing || editorBusy || deliveryBusy || directoryBusy;
  useEffect(() => {
    onReviewChange(step.kind === 'review');
    if (step.kind === 'review') setHasReviewed(true);
  }, [step.kind, onReviewChange]);
  const serviceGoal = step.goal === 'custom' ? 'crm' : step.goal === 'routing' ? 'transfer' : step.goal;
  async function moveTo(index) {
    if (busy) return;
    setRefreshing(true);
    setError('');
    try {
      await onRefresh();
      setEditorBusy(false);
      setDeliveryBusy(false);
      setDirectoryBusy(false);
      setStepIndex(index);
    } catch (_error) {
      setError('Saved settings could not be checked. Please try again.');
    } finally { setRefreshing(false); }
  }
  return <div className="onboarding-goal-setup">
    {error && <Alert tone="danger">{error}</Alert>}
    {connectionNotice === 'connected' && <Alert tone="success">Connected successfully. Continue your setup below.</Alert>}
    {connectionNotice === 'connection_failed' && <Alert tone="warning">The connection was not completed. Select your provider below to try again.</Alert>}
    {!session ? <>{!error && <LoadingState label="Loading setup settings…" />}{error && <Button onClick={load}>Try again</Button>}</> : <>
      <div className="onboarding-setup-heading">
        <span>{step.label} · {stepIndex + 1} of {steps.length}</span>
        <h2>{step.title}</h2>
      </div>
      {step.kind === 'connection' && step.goal === 'appointment' && <div>
        <CalendarIntegrations onboarding organization={session.organization} isAdmin={Boolean(session.member.is_org_admin || session.member.is_admin)} onOrganizationChange={updateOrganization} />
      </div>}
      {step.kind === 'connection' && step.goal === 'custom' && <CRMIntegrations embedded checkLogin={authenticated} orgMember={session.member} organization={session.organization} />}
      {step.kind === 'service' && <FeatureSection key={`${step.goal}-${revision}`} setupGoal={serviceGoal} organization={session.organization} orgMember={session.member} onBusyChange={setEditorBusy} />}
      {step.kind === 'delivery' && <MessageDeliverySettings compact orgMember={session.member} refreshVersion={revision} onBusyChange={setDeliveryBusy} />}
      {step.kind === 'team' && <ManageOrgMembersSection orgMember={session.member} onBusyChange={setDirectoryBusy} onMemberSaved={load} />}
      {step.kind === 'review' && <div className="onboarding-setup-summary">{paths.map(path => <section key={path.goal}>
        <div><h3>{path.label}</h3><span>{path.ready ? 'Ready' : 'Needs setup'}</span></div>
        {!path.ready && <ul>{path.requirements.filter(item => !item.ready).map(item => <li key={item.label}>{item.label}</li>)}</ul>}
        <Button variant="quiet" onClick={() => moveTo(Math.max(0, steps.findIndex(item => path.goal === 'faq' ? item.kind === 'delivery' : item.goal === path.goal)))}>Edit setup</Button>
      </section>)}</div>}
      <div className="onboarding-setup-navigation">
        <Button variant="quiet" disabled={busy} onClick={() => stepIndex ? moveTo(stepIndex - 1) : onBack()}>Back</Button>
        {step.kind !== 'review' && <div className="onboarding-setup-forward-actions">
          {stepIndex < steps.length - 2 && <Button variant="secondary" disabled={busy} onClick={() => moveTo(steps.length - 1)}>{hasReviewed ? 'Return to review' : 'Review setup'}</Button>}
          <Button disabled={busy} onClick={() => moveTo(stepIndex + 1)}>{stepIndex === steps.length - 2 ? (hasReviewed ? 'Return to review' : 'Review setup') : `Next: ${steps[stepIndex + 1].title.toLowerCase()}`}</Button>
        </div>}
      </div>
    </>}
  </div>;
}
